import {
  Box, IconButton, VStack, Text, Collapse, Flex, Avatar,
  Tooltip, Spinner, Input, InputGroup, InputLeftElement, InputRightElement, Divider
} from "@chakra-ui/react";
import {
  ChevronDown, ChevronUp, ChevronLeft, PackageSearch, ShoppingCart, Logs,
  LayoutList, ChartSpline, BookMarked, TableProperties, LogOut, Menu as MenuIcon,
  MessageCircleQuestion, Store, MessageSquareWarning, ShoppingBasket, Undo2,
  Ticket, TicketPlus, ChartNoAxesCombined, FileChartColumnIncreasing, FileChartLine,
  Search, Settings, UsersRound, Users, Unplug, BarChart3, FileSpreadsheet,
  Wallet, FolderTree, AlertTriangle, Sparkles, X, ShieldCheck, Bot, MessageSquare,
  Trophy, Image as ImageIcon, Ban, Trash2, HeartPulse, DollarSign, Phone, PackagePlus,
  Headset, BrainCircuit, TrendingUp, Columns3, Truck, LifeBuoy, Bookmark, PackageOpen, Ruler,
  ReceiptText, Scale, ArrowRightLeft, Mail, PhoneCall, Factory, ListChecks, Megaphone, FlaskConical
} from "lucide-react";

import { useState, useRef, useEffect, useContext, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useBreakpointValue } from "@chakra-ui/react";
import { AuthContext } from "../../contexts/auth";
import { podeVerDashboard } from "../../services/permission";
import { useAtalhos } from "../../contexts/atalhos";
import Preloader from "../Preloader";
import { FaFileInvoice } from "react-icons/fa";
import { MdAddShoppingCart, MdDiscount, MdOutlineInventory2 } from "react-icons/md";
import { MdShoppingCartCheckout } from "react-icons/md";
import { FaComputer } from "react-icons/fa6";
import { VscGraph } from "react-icons/vsc";
import { PiListChecksDuotone } from "react-icons/pi";
import { exportVendasDiaria, exportVendasMensal } from "../../services/api";

// ── Design tokens ──────────────────────────────────────────
const BG        = "#1E1E2D";
const ACCENT    = "#F68633";
const HOVER_BG  = "rgba(255,255,255,0.07)";
const ACTIVE_BG = "rgba(246,134,51,0.13)";
const MUTED     = "#9CA3AF";
const BORDER    = "rgba(255,255,255,0.08)";
const W_OPEN    = "260px";
const W_CLOSED  = "64px";
const NAV_TOP   = "56px"; // altura da NavbarTop
// ────────────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <Text
    fontSize="9px" fontWeight="bold" letterSpacing="widest"
    color={MUTED} px={2} pt={4} pb={1}
    textTransform="uppercase" userSelect="none"
  >
    {children}
  </Text>
);

/**
 * Bandeira que fixa/solta a tela nos atalhos da navbar (desktop).
 * Fica escondida ate o hover da linha, para nao poluir o menu, e some no
 * mobile porque a barra de atalhos so existe no desktop.
 */
const BotaoAtalho = ({ link, texto }) => {
  const { temAtalho, alternarAtalho, cheio, max, salvando } = useAtalhos();

  if (!link || link.includes('?')) return null;

  const fixado = temAtalho(link);
  const gravando = salvando(link);
  const bloqueado = !fixado && cheio;

  return (
    <Tooltip
      label={gravando ? "Salvando..." : (fixado ? "Remover dos atalhos" : (bloqueado ? `Limite de ${max} atalhos atingido` : "Fixar nos atalhos"))}
      placement="right" hasArrow bg="gray.800" fontSize="xs" openDelay={400}
    >
      <Box
        as="button"
        type="button"
        display={{ base: "none", lg: "flex" }}
        alignItems="center" justifyContent="center"
        w="13px" ml={1} flexShrink={0}
        // Enquanto grava, fica visível mesmo sem hover, para o usuário ver o progresso.
        opacity={fixado || gravando ? 1 : 0}
        _groupHover={{ opacity: bloqueado ? 0.35 : 1 }}
        color={fixado ? ACCENT : MUTED}
        cursor={gravando ? "wait" : (bloqueado ? "not-allowed" : "pointer")}
        transition="opacity 0.15s, color 0.15s"
        aria-label={fixado ? `Remover ${texto} dos atalhos` : `Fixar ${texto} nos atalhos`}
        aria-busy={gravando}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!bloqueado && !gravando) alternarAtalho(link, texto);
        }}
      >
        {gravando
          ? <Spinner size="xs" speed="0.6s" thickness="2px" color={ACCENT} boxSize="11px" />
          : <Bookmark size={13} fill={fixado ? ACCENT : "none"} />}
      </Box>
    </Tooltip>
  );
};

const matchesLink = (link, currentPath, currentSearch) => {
  if (!link) return false;
  const full = currentPath + currentSearch;
  if (link.includes('?')) return full === link;
  return full === link ||
         (link !== "/" && full.startsWith(link + "/")) ||
         (link !== "/" && currentPath.startsWith(link + "/") && !currentSearch);
};

const NavItem = ({
  link, icon, text, hasSubmenu, open, toggleSubmenu,
  sublinks, toggleSidebar, downloadingReport, sidebarOpen, currentPath, currentSearch,
  badge,
}) => {
  const isActive = !hasSubmenu && matchesLink(link, currentPath, currentSearch);
  const isGroupActive = hasSubmenu &&
    sublinks?.some(s => matchesLink(s.link, currentPath, currentSearch));

  if (!sidebarOpen) {
    return (
      <Tooltip label={text} placement="right" hasArrow bg="gray.800" color="white" fontSize="xs" openDelay={200}>
        <Box position="relative" mx="auto" w="40px" h="40px">
          <Flex
            as={link && !hasSubmenu ? Link : "div"}
            to={link}
            align="center" justify="center"
            w="40px" h="40px" borderRadius="lg"
            bg={isActive || isGroupActive ? ACTIVE_BG : "transparent"}
            borderLeft={`3px solid ${isActive || isGroupActive ? ACCENT : "transparent"}`}
            color={isActive || isGroupActive ? ACCENT : "white"}
            _hover={{ bg: HOVER_BG, cursor: "pointer", color: ACCENT }}
            onClick={hasSubmenu ? toggleSidebar : undefined}
            transition="all 0.15s" flexShrink={0}
          >
            {icon}
          </Flex>
          {badge > 0 && (
            <Box position="absolute" top="-2px" right="-2px"
              bg="red.500" color="white" borderRadius="full"
              minW="16px" h="16px" fontSize="9px" fontWeight="bold"
              display="flex" alignItems="center" justifyContent="center" px="3px"
            >
              {badge > 99 ? '99+' : badge}
            </Box>
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box>
      <Flex
        as={link && !hasSubmenu ? Link : "div"}
        to={link}
        align="center" px={3} py={2} borderRadius="lg"
        bg={isActive || isGroupActive ? ACTIVE_BG : "transparent"}
        borderLeft={`3px solid ${isActive || isGroupActive ? ACCENT : "transparent"}`}
        color={isActive || isGroupActive ? ACCENT : "white"}
        _hover={{ bg: HOVER_BG, cursor: "pointer", color: ACCENT }}
        onClick={hasSubmenu ? toggleSubmenu : () => { if (link) toggleSidebar(); }}
        transition="all 0.15s" role="group"
      >
        <Box
          color={isActive || isGroupActive ? ACCENT : MUTED}
          flexShrink={0} _groupHover={{ color: ACCENT }} transition="color 0.15s"
        >
          {icon}
        </Box>
        <Text
          fontSize="sm"
          fontWeight={isActive || isGroupActive ? "semibold" : "medium"}
          ml={3} flex="1" isTruncated
        >
          {text}
        </Text>
        {badge > 0 && (
          <Box bg="red.500" color="white" borderRadius="full"
            minW="18px" h="18px" fontSize="10px" fontWeight="bold"
            display="flex" alignItems="center" justifyContent="center" px="4px" ml={1}
          >
            {badge > 99 ? '99+' : badge}
          </Box>
        )}
        {!hasSubmenu && <BotaoAtalho link={link} texto={text} />}
        {hasSubmenu && (
          <Box color={MUTED} flexShrink={0} ml={1}>
            {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </Box>
        )}
      </Flex>

      {hasSubmenu && (
        <Collapse in={open} animateOpacity>
          <VStack spacing={0} align="stretch" pl={8} pt={0.5} pb={1}>
            {sublinks.map((sublink, i) => {
              const isSub = matchesLink(sublink.link, currentPath, currentSearch);
              return sublink.link ? (
                <Link to={sublink.link} key={i} onClick={toggleSidebar}>
                  <Flex
                    align="center" px={3} py={1.5} borderRadius="md"
                    bg={isSub ? ACTIVE_BG : "transparent"}
                    borderLeft={`2px solid ${isSub ? ACCENT : "transparent"}`}
                    color={isSub ? ACCENT : MUTED}
                    _hover={{ bg: HOVER_BG, color: "white", cursor: "pointer" }}
                    transition="all 0.12s" role="group"
                  >
                    <Box flexShrink={0} opacity={isSub ? 1 : 0.75}>{sublink.icon}</Box>
                    <Text ml={2} fontSize="xs" fontWeight={isSub ? "semibold" : "normal"} flex="1" isTruncated>
                      {sublink.text}
                    </Text>
                    <BotaoAtalho link={sublink.link} texto={sublink.text} />
                  </Flex>
                </Link>
              ) : (
                <Flex
                  key={i} align="center" px={3} py={1.5} borderRadius="md"
                  color={MUTED} _hover={{ bg: HOVER_BG, color: "white", cursor: "pointer" }}
                  onClick={sublink.onClick} transition="all 0.12s"
                >
                  <Box flexShrink={0} opacity={0.75}>{sublink.icon}</Box>
                  {downloadingReport === sublink.id ? (
                    <Spinner size="xs" ml={2} color={ACCENT} />
                  ) : (
                    <Text ml={2} fontSize="xs">{sublink.text}</Text>
                  )}
                </Flex>
              );
            })}
          </VStack>
        </Collapse>
      )}
    </Box>
  );
};

// ── Main component ───────────────────────────────────────────
const Sidebar = () => {
  const [loading, setLoading]           = useState(false);
  const { logout, user }                = useContext(AuthContext);
  const isMobile                        = useBreakpointValue({ base: true, md: false });
  const [downloadingReport, setDownloadingReport] = useState(null);
  const [searchQuery, setSearchQuery]   = useState("");
  const location                        = useLocation();
  const currentPath                     = location.pathname;

  const handleDownload = async (reportType) => {
    setDownloadingReport(reportType);
    try {
      let blob, filename;
      if (reportType === "diario") {
        blob = await exportVendasDiaria();
        filename = `relatorio-vendas-diario-${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (reportType === "mensal") {
        blob = await exportVendasMensal();
        filename = `relatorio-vendas-mensal-${new Date().toISOString().slice(0, 7)}.xlsx`;
      }
      if (blob) {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const a = document.createElement("a");
        a.href = url; a.setAttribute("download", filename);
        document.body.appendChild(a); a.click();
        a.parentNode.removeChild(a); window.URL.revokeObjectURL(url);
      }
    } catch (e) { console.error(e); }
    finally { setDownloadingReport(null); }
  };

  // Checa permissao do usuario pela lista de permissoes
  const perms = user.permissoes || [];
  const isAdmin = user?.tipo === 1;
  // Admin passa sempre; fora isso, a pagina precisa estar na lista. Lista
  // vazia NEGA (mesma regra do backend, middleware PermissaoPagina): mostrar
  // o menu inteiro para quem toma 403 em tudo e pior do que mostrar menos.
  const hasPerm = (pagina) => {
    if (pagina === "/dashboard") return podeVerDashboard(user); // liberada por tipo (blocos)
    if (isAdmin) return true;
    const rota = String(pagina || "").trim();
    if (!rota) return false;
    return perms.includes(rota);
  };

  const getImgUser = () => {
    const map = { 3: "/gabriel_logo.webp", 5: "/tilio_logo.webp", 6: "/riccioppo_logo.webp", 8: "/gabriel_valiente_logo.webp", 9: "/emerson_vendedor_logo.webp", 10: "/analuiza_logo.webp" };
    return map[user.id] || "/user.png";
  };

  const tipoLabel = { 1: "Administrador", 2: "Vendedor", 3: "Operações", 4: "Suporte" };

  const handleLogout = () => {
    setLoading(true);
    logout();
    setTimeout(() => setLoading(false), 300);
  };

  const [isOpen, setIsOpen]           = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState({});
  const toggleSidebar  = () => setIsOpen(v => !v);
  const toggleSubmenu  = (menu) => setOpenSubmenu(prev => ({ ...prev, [menu]: !prev[menu] }));

  const sidebarRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Lista completa — filtra automaticamente por permissoes do usuario.
  // `admin: true` = rota AdminOnly: so admin ve, mesmo que a permissao esteja
  // gravada para outro usuario (igual ao isAdmin do menu).
  const allSearchItems = [
    { text: "Dashboard",              link: "/dashboard",                section: "Análise",      perm: "/dashboard" },
    { text: "DRE Consolidado",        link: "/dre",                      section: "Análise · Financeiro",      perm: "/dre" },
    { text: "DRE Detalhado",          link: "/dre/detalhado",            section: "Análise · Financeiro",      perm: "/dre" },
    { text: "DRE Custos",             link: "/dre/custos",               section: "Análise · Financeiro",      perm: "/dre" },
    { text: "DRE Categorias",         link: "/dre/categorias",           section: "Análise · Financeiro",      perm: "/dre" },
    { text: "Furo Estoque",           link: "/dre/furo-estoque",         section: "Análise · Financeiro",      perm: "/dre" },
    { text: "Desempenho Atendentes",  link: "/desempenho",               section: "Análise",      perm: "/desempenho" },
    { text: "Comparador de Preços",   link: "/comparador-precos",        section: "Análise · Inteligência",      perm: "/comparador-precos" },
    { text: "Compra Inteligente",     link: "/compra-inteligente",       section: "Análise · Inteligência",      perm: "/compra-inteligente", admin: true },
    { text: "Precificação Inteligente", link: "/precificacao-inteligente", section: "Análise · Inteligência",    perm: "/precificacao-inteligente", admin: true },
    { text: "Vendas",                 link: "/vendas",                   section: "Pedidos · Vendas",      perm: "/vendas" },
    { text: "Vendas Vendedor",        link: "/vendas_vendedor",          section: "Pedidos · Vendas",      perm: "/vendas_vendedor" },
    { text: "Venda Manual",           link: "/venda_manual",             section: "Pedidos · Vendas",      perm: "/venda_manual" },
    { text: "Notas Fiscais",          link: "/notas_fiscais",            section: "Pedidos · Vendas",      perm: "/notas_fiscais" },
    { text: "Devoluções",             link: "/devolucoes",               section: "Pedidos · Pós-Venda",      perm: "/devolucoes" },
    { text: "Devolução a Fornecedor", link: "/devolucoes-fornecedor",    section: "Pedidos · Pós-Venda",      perm: "/devolucoes-fornecedor" },
    { text: "NF-e de Devolução do Cliente", link: "/nfe-devolucao-cliente", section: "Pedidos · Pós-Venda",      perm: "/nfe-devolucao-cliente" },
    { text: "Reclamações",            link: "/reclamacoes",              section: "Pedidos · Pós-Venda",      perm: "/reclamacoes" },
    { text: "Listas de Compras",      link: "/listas-compras/dashboard", section: "Pedidos · Compras",      perm: "/listas-compras/dashboard" },
    { text: "Kanban de Compras",      link: "/listas-compras/kanban",    section: "Pedidos · Compras",      perm: "/listas-compras/dashboard" },
    { text: "Acompanhamento Pedidos", link: "/acompanhamento_pedidos",   section: "Pedidos · Expedição",      perm: "/acompanhamento_pedidos" },
    { text: "Gestão de Despacho",     link: "/despacho",                 section: "Pedidos · Expedição",      perm: "/despacho" },
    { text: "Pendências de Envio",   link: "/pendencias-vendas",        section: "Pedidos · Expedição",      perm: "/pendencias-vendas" },
    { text: "Monit. Processamento", link: "/processador/monitoramento", section: "Pedidos · Expedição",   perm: "/processador/monitoramento" },
    { text: "Produtos",               link: "/produtos",                 section: "Catálogo",     perm: "/produtos" },
    { text: "Produtos Bloqueados",    link: "/produtos-bloqueados",      section: "Catálogo",     perm: "/produtos-bloqueados" },
    { text: "Controle de Estoque",    link: "/gestao_estoque",           section: "Catálogo",     perm: "/gestao_estoque" },
    { text: "Revisão de Estoque",     link: "/revisao-estoque",          section: "Catálogo",     perm: "/revisao-estoque" },
    { text: "Transferência de Estoque", link: "/transferencia-estoque",  section: "Catálogo",     perm: "/transferencia-estoque" },
    { text: "Entrada Estoque",        link: "/entrada-estoque",          section: "Catálogo",     perm: "/entrada-estoque" },
    { text: "Promoções",              link: "/promocoes",                section: "Catálogo",     perm: "/promocoes" },
    { text: "Publicidade ML",         link: "/publicidade-ml",           section: "Catálogo",     perm: "/publicidade-ml", admin: true },
    { text: "Saúde dos Anúncios ML",  link: "/saude-anuncios",           section: "Catálogo",     perm: "/saude-anuncios", admin: true },
    { text: "Métricas de Anúncios",   link: "/metricas-anuncios",        section: "Catálogo",     perm: "/metricas-anuncios", admin: true },
    { text: "Orçamentos",             link: "/orcamentos",               section: "Pedidos · Vendas",  perm: "/orcamentos" },
    { text: "Denúncias",              link: "/anuncios/denuncias",       section: "Pedidos · Pós-Venda", perm: "/anuncios/denuncias" },
    { text: "Banco de Imagens",        link: "/banco-imagens",             section: "Catálogo",    perm: "/banco-imagens" },
    { text: "Dimensões",              link: "/dimensoes",                 section: "Catálogo",    perm: "/dimensoes" },
    { text: "Imagens Excluídas",      link: "/banco-imagens?excluidas=1", section: "Catálogo",    perm: "/banco-imagens" },
    { text: "Logs de Imagens",        link: "/logs-imagens",              section: "Catálogo",    perm: "/banco-imagens" },
    { text: "Imagens Bloqueadas",     link: "/imagens-bloqueadas",        section: "Catálogo",    perm: "/imagens-bloqueadas" },
    { text: "Migração de Imagens",    link: "/migracao-imagens",           section: "Catálogo",    perm: "/migracao-imagens", admin: true },
    { text: "Compatibilidade (Catálogo Oficial)", link: "/compatibilidade-catalogo", section: "Pedidos · Expedição", perm: "/compatibilidade-catalogo" },
    { text: "Nova Cotação",           link: "/cotacoes_automaticas",     section: "Pedidos · Compras",  perm: "/cotacoes_automaticas" },
    { text: "Cotações",               link: "/cotacoes",                 section: "Pedidos · Compras",  perm: "/cotacoes" },
    { text: "Cobertura de Furos",     link: "/cobertura-furos",          section: "Pedidos · Compras",  perm: "/cobertura-furos" },
    { text: "Compras de Fábrica",     link: "/compras-fabrica",          section: "Pedidos · Compras",  perm: "/compras-fabrica" },
    { text: "Divergências de Preço",  link: "/divergencias-preco",       section: "Pedidos · Compras",  perm: "/divergencias-preco", admin: true },
    { text: "Clientes",               link: "/clientes",                 section: "Clientes",     perm: "/clientes" },
    { text: "CRM",                    link: "/crm",                      section: "Clientes",     perm: "/crm" },
    { text: "Prospecção",             link: "/prospeccao",               section: "Clientes",     perm: "/prospeccao" },
    { text: "Mensageria",             link: "/mensageria",               section: "Clientes · Atendimento", perm: "/mensageria" },
    { text: "Perguntas",              link: "/perguntas",                section: "Clientes · Atendimento", perm: "/perguntas" },
    { text: "Cotações por E-mail",    link: "/cotacoes-email",           section: "Clientes · Atendimento", perm: "/cotacoes-email" },
    { text: "Solicitações",           link: "/solicitacoes",             section: "Clientes · Atendimento", perm: "/assistente-vendas" },
    { text: "Discador NVOIP",         link: "/discador",                 section: "Clientes · Telefone",    perm: "/discador" },
    { text: "URA do telefone",        link: "/ura",                      section: "Clientes · Telefone",    perm: "/ura" },
    { text: "Assistente Poli",        link: "/assistente-vendas",        section: "Clientes · Bot Poli",    perm: "/assistente-vendas" },
    { text: "Chat IA",                link: "/chat-ia",                  section: "Clientes · Bot Poli",    perm: "/chat-ia" },
    { text: "Bot Atendimento",        link: "/bot-atendimento",          section: "Clientes · Bot Poli",    perm: "/assistente-vendas" },
    { text: "Custos IA (Bot)",        link: "/bot-custos-ia",            section: "Clientes · Bot Poli",    perm: "/bot-custos-ia", admin: true },
    { text: "Teste A/B do Bot",       link: "/bot-teste-ab",             section: "Clientes · Bot Poli",    perm: "/bot-teste-ab", admin: true },
    { text: "Melhor Envio",           link: "/melhor-envio",             section: "Integrações",  perm: "/melhor-envio" },
    { text: "Lalamove",               link: "/lalamove",                 section: "Integrações",  perm: "/lalamove" },
    { text: "Tickets",                link: "/tickets",                  section: "Suporte",      perm: "/tickets" },
    { text: "Novo Ticket",            link: "/tickets/novo",             section: "Suporte",      perm: "/tickets" },
    { text: "Exportar Produtos",      link: "/exportar_produtos",        section: "Relatórios",   perm: "/exportar_produtos" },
    { text: "Exportar Clientes",      link: "/exportar_clientes",        section: "Relatórios",   perm: "/exportar_clientes" },
    { text: "Exportar Vendas",        link: "/exportar_vendas",          section: "Relatórios",   perm: "/exportar_vendas" },
    { text: "Exportar Conversas",     link: "/exportar_conversas",       section: "Relatórios",   perm: "/exportar_conversas" },
    { text: "Exportar Listas de Compras", link: "/exportar_listas_compras", section: "Relatórios", perm: "/exportar_listas_compras" },
    { text: "Exportar Movimentação de Estoque", link: "/exportar_movimentacao_estoque", section: "Relatórios", perm: "/exportar_movimentacao_estoque", admin: true },
    { text: "Exportar XML das NF-e", link: "/notas_fiscais?aba=exportar", section: "Relatórios", perm: "/exportar_xml_nfe", admin: true },
    { text: "Usuários",               link: "/usuarios",                 section: "Sistema · Usuários",      perm: "/usuarios" },
    { text: "Gestão de Acessos",     link: "/log-acessos",              section: "Sistema · Usuários",      perm: "/log-acessos" },
    { text: "Configurações",          link: "/configuracoes",            section: "Sistema",                 perm: "/configuracoes" },
    { text: "Servidor",               link: "/servidor",                 section: "Sistema · Monitoramento", perm: "/servidor" },
    { text: "Logs Sistema",           link: "/logs",                     section: "Sistema · Monitoramento", perm: "/logs" },
    { text: "Logs Produtos",          link: "/logs_produtos",            section: "Sistema · Monitoramento", perm: "/logs_produtos" },
    { text: "Wiki",                   link: "/wiki",                     section: "Sistema · Ajuda",         perm: "/wiki" },
    { text: "Guias",                  link: "/guias",                    section: "Sistema · Ajuda",         perm: "/guias" },
  ];

  const searchItems = useMemo(() => {
    return allSearchItems.filter(item => (item.admin ? isAdmin : hasPerm(item.perm)));
  }, [perms]); // eslint-disable-line

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    // Sem acento e sem caixa ("cotacao" acha "Cotações"), e olhando também a
    // seção ("poli" lista as telas do bot, "pos-venda" as do pós-venda).
    const normalizar = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    const q = normalizar(searchQuery.trim());
    return searchItems.filter(item => normalizar(`${item.text} ${item.section}`).includes(q));
  }, [searchQuery, searchItems]);

  const currentSearch = location.search;

  // Shared props passed to every NavItem
  const nav = { toggleSidebar, sidebarOpen: isOpen, currentPath, currentSearch, downloadingReport };

  return (
    <>
      {loading ? <Preloader /> : (
        <Box>
          {/* Backdrop */}
          {isOpen && (
            <Box
              position="fixed" top={NAV_TOP} left="0" w="100%"
              h={`calc(100vh - ${NAV_TOP})`}
              bg="rgba(0,0,0,0.5)" zIndex="997" onClick={toggleSidebar}
            />
          )}

          {/* Mobile hamburger */}
          {isMobile && !isOpen && (
            <IconButton
              position="fixed" top="65px" left="20px" zIndex="1001"
              icon={<MenuIcon size={22} />} onClick={toggleSidebar}
              aria-label="Abrir Menu" bg={ACCENT} color="white"
              _hover={{ bg: "#d4702a" }} h="44px" w="44px"
              borderRadius="full" boxShadow="lg"
              display={{ base: "flex", md: "none" }}
            />
          )}

          {/* ── Sidebar panel ── */}
          <Box
            ref={sidebarRef}
            as="nav"
            position="fixed"
            top={NAV_TOP}
            left={isMobile ? (isOpen ? "0" : "-260px") : "0"}
            h={`calc(100vh - ${NAV_TOP})`}
            w={isMobile ? W_OPEN : (isOpen ? W_OPEN : W_CLOSED)}
            bg={BG}
            color="white"
            zIndex="998"
            transition="all 0.25s ease"
            display="flex"
            flexDirection="column"
            boxShadow="2xl"
            overflow="hidden"
          >
            {/* ── User section (TOPO) ── */}
            <Flex
              align="center"
              px={isOpen ? 3 : 0}
              py={3}
              borderBottom={`1px solid ${BORDER}`}
              justify={isOpen ? "flex-start" : "center"}
              flexShrink={0}
              gap={isOpen ? 3 : 0}
              minH="64px"
            >
              <Tooltip label={!isOpen ? `${user.name} — clique para expandir` : ""} placement="right" isDisabled={isOpen}>
                <Avatar
                  size="sm" name={user.name} src={getImgUser()}
                  cursor={!isOpen ? "pointer" : "default"}
                  flexShrink={0}
                  onClick={!isOpen ? toggleSidebar : undefined}
                  onMouseEnter={!isOpen ? toggleSidebar : undefined}
                  border={`2px solid ${ACCENT}`}
                />
              </Tooltip>
              {isOpen && (
                <>
                  <Box flex="1" minW={0}>
                    <Text fontSize="sm" fontWeight="semibold" color="white" isTruncated lineHeight={1.3}>
                      {user.name}
                    </Text>
                    <Text fontSize="10px" color={MUTED} mt="2px">
                      {tipoLabel[user.tipo] || "Usuário"}
                    </Text>
                  </Box>
                  <Tooltip label="Sair" placement="top">
                    <IconButton
                      icon={<LogOut size={15} />} size="sm" variant="ghost"
                      color={MUTED}
                      _hover={{ color: "#F87171", bg: "rgba(248,113,113,0.1)" }}
                      onClick={handleLogout} aria-label="Logout" flexShrink={0}
                    />
                  </Tooltip>
                </>
              )}
            </Flex>

            {/* ── Search (só quando expandida) ── */}
            {isOpen && (
              <Box px={3} py={2.5} borderBottom={`1px solid ${BORDER}`} flexShrink={0}>
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none" h="32px">
                    <Search size={13} color={MUTED} />
                  </InputLeftElement>
                  <Input
                    h="32px"
                    placeholder="Buscar ferramenta..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    bg="rgba(255,255,255,0.06)"
                    border={`1px solid ${BORDER}`}
                    _placeholder={{ color: MUTED, fontSize: "12px" }}
                    color="white"
                    _hover={{ borderColor: "rgba(255,255,255,0.2)" }}
                    _focus={{ borderColor: ACCENT, boxShadow: "none", bg: "rgba(255,255,255,0.09)" }}
                    borderRadius="lg"
                    fontSize="13px"
                  />
                  {searchQuery && (
                    <InputRightElement h="32px">
                      <IconButton
                        icon={<X size={11} />} size="xs" variant="ghost"
                        color={MUTED} _hover={{ color: "white", bg: "transparent" }}
                        onClick={() => setSearchQuery("")}
                        aria-label="Limpar busca" minW="unset" h="auto"
                      />
                    </InputRightElement>
                  )}
                </InputGroup>
              </Box>
            )}

            {/* ── Nav (scrollable) ── */}
            <Box
              flex="1" overflowY="auto" py={2}
              px={isOpen ? 3 : 2}
              sx={{
                "&::-webkit-scrollbar": { width: "3px" },
                "&::-webkit-scrollbar-track": { bg: "transparent" },
                "&::-webkit-scrollbar-thumb": { bg: "rgba(255,255,255,0.12)", borderRadius: "10px" },
              }}
            >
              {/* ── Search results ── */}
              {isOpen && searchQuery ? (
                <VStack spacing={0.5} align="stretch">
                  {searchResults.length === 0 ? (
                    <Text color={MUTED} fontSize="12px" px={2} py={6} textAlign="center">
                      Nenhum resultado para "{searchQuery}"
                    </Text>
                  ) : searchResults.map((item, i) => {
                    const isActive = currentPath === item.link || (item.link !== "/" && currentPath.startsWith(item.link + "/"));
                    return (
                      <Link to={item.link} key={i} onClick={() => { toggleSidebar(); setSearchQuery(""); }}>
                        <Flex
                          align="center" px={3} py={2} borderRadius="lg"
                          bg={isActive ? ACTIVE_BG : "transparent"}
                          borderLeft={`3px solid ${isActive ? ACCENT : "transparent"}`}
                          _hover={{ bg: HOVER_BG, cursor: "pointer" }}
                          transition="all 0.12s"
                        >
                          <Box flex="1" minW={0}>
                            <Text fontSize="sm" fontWeight={isActive ? "semibold" : "medium"} color={isActive ? ACCENT : "white"} isTruncated>
                              {item.text}
                            </Text>
                            <Text fontSize="9px" color={MUTED} letterSpacing="wide" textTransform="uppercase">
                              {item.section}
                            </Text>
                          </Box>
                        </Flex>
                      </Link>
                    );
                  })}
                </VStack>
              ) : (
                <VStack spacing={0.5} align="stretch">
                  {/* ── Análise ── */}
                  {(hasPerm("/dashboard") || hasPerm("/dre") || hasPerm("/desempenho") || hasPerm("/comparador-precos") || isAdmin) && (
                    <>
                      {isOpen && <SectionLabel>Análise</SectionLabel>}
                      {hasPerm("/dashboard") && <NavItem icon={<ChartSpline size={20} />} text="Dashboard" link="/dashboard" hasSubmenu={false} {...nav} />}
                      {hasPerm("/dre") && (
                        <NavItem
                          icon={<BarChart3 size={20} />} text="DRE" hasSubmenu
                          open={openSubmenu.dre} toggleSubmenu={() => toggleSubmenu("dre")}
                          sublinks={[
                            { text: "Consolidado",  link: "/dre",             icon: <FileSpreadsheet size={14} /> },
                            { text: "Detalhado",    link: "/dre/detalhado",   icon: <BarChart3 size={14} /> },
                            { text: "Custos",       link: "/dre/custos",      icon: <Wallet size={14} /> },
                            { text: "Categorias",   link: "/dre/categorias",  icon: <FolderTree size={14} /> },
                            { text: "Furo Estoque", link: "/dre/furo-estoque",icon: <AlertTriangle size={14} /> },
                          ]}
                          {...nav}
                        />
                      )}
                      {/* Preços e compras: as três telas de IA que decidem quanto
                          cobrar e de quem comprar. Compra e Precificação são
                          restritas a admin (AdminOnly na rota). */}
                      {(hasPerm("/comparador-precos") || isAdmin) && (
                        <NavItem
                          icon={<Sparkles size={20} />} text="Inteligência" hasSubmenu
                          open={openSubmenu.inteligencia} toggleSubmenu={() => toggleSubmenu("inteligencia")}
                          sublinks={[
                            hasPerm("/comparador-precos") && { text: "Comparador de Preços",     link: "/comparador-precos",        icon: <DollarSign size={14} /> },
                            isAdmin                       && { text: "Precificação Inteligente", link: "/precificacao-inteligente", icon: <TrendingUp size={14} /> },
                            isAdmin                       && { text: "Compra Inteligente",       link: "/compra-inteligente",       icon: <BrainCircuit size={14} /> },
                          ].filter(Boolean)}
                          {...nav}
                        />
                      )}
                      {hasPerm("/desempenho") && <NavItem icon={<Trophy size={20} />} text="Desempenho Atendentes" link="/desempenho" hasSubmenu={false} {...nav} />}
                    </>
                  )}

                  {/* ── Pedidos ── */}
                  {["/vendas","/vendas_vendedor","/venda_manual","/notas_fiscais","/devolucoes","/devolucoes-fornecedor","/nfe-devolucao-cliente","/reclamacoes","/listas-compras/dashboard","/acompanhamento_pedidos","/despacho","/pendencias-vendas","/processador/monitoramento","/anuncios/denuncias","/orcamentos","/compatibilidade-catalogo","/cotacoes_automaticas","/cotacoes","/cobertura-furos","/divergencias-preco","/compras-fabrica"].some(hasPerm) && (
                    <>
                      {isOpen && <SectionLabel>Pedidos</SectionLabel>}

                      {["/vendas", "/vendas_vendedor", "/venda_manual", "/notas_fiscais", "/orcamentos"].some(hasPerm) && (
                        <NavItem
                          icon={<ShoppingCart size={20} />} text="Vendas" hasSubmenu
                          open={openSubmenu.pedidosVendas} toggleSubmenu={() => toggleSubmenu("pedidosVendas")}
                          sublinks={[
                            hasPerm("/vendas")          && { text: "Vendas",          link: "/vendas",          icon: <ShoppingCart size={14} /> },
                            hasPerm("/vendas_vendedor") && { text: "Vendas Vendedor", link: "/vendas_vendedor", icon: <Store size={14} /> },
                            hasPerm("/venda_manual")    && { text: "Venda Manual",    link: "/venda_manual",    icon: <ShoppingBasket size={14} /> },
                            hasPerm("/orcamentos")      && { text: "Orçamentos",      link: "/orcamentos",      icon: <FileSpreadsheet size={14} /> },
                            hasPerm("/notas_fiscais")   && { text: "Notas Fiscais",   link: "/notas_fiscais",   icon: <FaFileInvoice size={14} /> },
                          ].filter(Boolean)}
                          {...nav}
                        />
                      )}

                      {["/listas-compras/dashboard", "/cotacoes_automaticas", "/cotacoes", "/cobertura-furos", "/divergencias-preco", "/compras-fabrica"].some(hasPerm) && (
                        <NavItem
                          icon={<PackagePlus size={20} />} text="Compras" hasSubmenu
                          open={openSubmenu.pedidosCompras} toggleSubmenu={() => toggleSubmenu("pedidosCompras")}
                          sublinks={[
                            hasPerm("/listas-compras/dashboard") && { text: "Listas de Compras", link: "/listas-compras/dashboard", icon: <ShoppingCart size={14} /> },
                            hasPerm("/listas-compras/dashboard") && { text: "Kanban de Compras", link: "/listas-compras/kanban",    icon: <Columns3 size={14} /> },
                            hasPerm("/cotacoes_automaticas")     && { text: "Nova Cotação",      link: "/cotacoes_automaticas",     icon: <MdAddShoppingCart size={14} style={{ transform: "scaleX(-1)" }} /> },
                            hasPerm("/cotacoes")                 && { text: "Cotações",          link: "/cotacoes",                 icon: <MdShoppingCartCheckout size={14} style={{ transform: "scaleX(-1)" }} /> },
                            hasPerm("/cobertura-furos")          && { text: "Cobertura de Furos", link: "/cobertura-furos",         icon: <MdOutlineInventory2 size={14} /> },
                            hasPerm("/compras-fabrica")          && { text: "Compras de Fábrica", link: "/compras-fabrica",         icon: <Factory size={14} /> },
                            isAdmin                              && { text: "Divergências de Preço", link: "/divergencias-preco",     icon: <Scale size={14} /> },
                          ].filter(Boolean)}
                          {...nav}
                        />
                      )}

                      {["/despacho", "/pendencias-vendas", "/acompanhamento_pedidos", "/compatibilidade-catalogo", "/processador/monitoramento"].some(hasPerm) && (
                        <NavItem
                          icon={<Truck size={20} />} text="Expedição" hasSubmenu
                          open={openSubmenu.pedidosExpedicao} toggleSubmenu={() => toggleSubmenu("pedidosExpedicao")}
                          sublinks={[
                            hasPerm("/despacho")                  && { text: "Gestão de Despacho",   link: "/despacho",                  icon: <PiListChecksDuotone size={14} /> },
                            hasPerm("/pendencias-vendas")         && { text: "Pendências de Envio",  link: "/pendencias-vendas",         icon: <AlertTriangle size={14} /> },
                            hasPerm("/acompanhamento_pedidos")    && { text: "Acompanhamento",       link: "/acompanhamento_pedidos",    icon: <VscGraph size={14} /> },
                            hasPerm("/compatibilidade-catalogo")  && { text: "Compatibilidade",      link: "/compatibilidade-catalogo",  icon: <ShieldCheck size={14} /> },
                            hasPerm("/processador/monitoramento") && { text: "Monit. Processamento", link: "/processador/monitoramento", icon: <VscGraph size={14} /> },
                          ].filter(Boolean)}
                          {...nav}
                        />
                      )}

                      {["/devolucoes", "/devolucoes-fornecedor", "/nfe-devolucao-cliente", "/reclamacoes", "/anuncios/denuncias"].some(hasPerm) && (
                        <NavItem
                          icon={<LifeBuoy size={20} />} text="Pós-Venda" hasSubmenu
                          open={openSubmenu.pedidosPosVenda} toggleSubmenu={() => toggleSubmenu("pedidosPosVenda")}
                          sublinks={[
                            hasPerm("/devolucoes")         && { text: "Devoluções",  link: "/devolucoes",         icon: <Undo2 size={14} /> },
                            hasPerm("/devolucoes-fornecedor") && { text: "Devolução a Fornecedor", link: "/devolucoes-fornecedor", icon: <PackageOpen size={14} /> },
                            hasPerm("/nfe-devolucao-cliente") && { text: "NF-e Devolução Cliente", link: "/nfe-devolucao-cliente", icon: <ReceiptText size={14} /> },
                            hasPerm("/reclamacoes")        && { text: "Reclamações", link: "/reclamacoes",        icon: <MessageSquareWarning size={14} /> },
                            hasPerm("/anuncios/denuncias") && { text: "Denúncias",   link: "/anuncios/denuncias", icon: <Ban size={14} /> },
                          ].filter(Boolean)}
                          {...nav}
                        />
                      )}
                    </>
                  )}

                  {/* ── Catálogo ── */}
                  {(["/produtos","/produtos-bloqueados","/gestao_estoque","/revisao-estoque","/transferencia-estoque","/entrada-estoque","/promocoes","/banco-imagens","/imagens-bloqueadas","/migracao-imagens","/dimensoes"].some(hasPerm) || isAdmin) && (
                    <>
                      {isOpen && <SectionLabel>Catálogo</SectionLabel>}
                      {(["/produtos","/produtos-bloqueados","/gestao_estoque","/revisao-estoque","/transferencia-estoque","/entrada-estoque","/promocoes"].some(hasPerm) || isAdmin) && (
                        <NavItem
                          icon={<TableProperties size={20} />} text="Catálogo" hasSubmenu
                          open={openSubmenu.catalogo} toggleSubmenu={() => toggleSubmenu("catalogo")}
                          sublinks={[
                            hasPerm("/produtos")          && { text: "Produtos",           link: "/produtos",           icon: <LayoutList size={14} /> },
                            hasPerm("/produtos-bloqueados") && { text: "Produtos Bloqueados", link: "/produtos-bloqueados", icon: <Ban size={14} /> },
                            hasPerm("/gestao_estoque")    && { text: "Controle de Estoque",link: "/gestao_estoque",     icon: <PackageSearch size={14} /> },
                            hasPerm("/revisao-estoque")   && { text: "Revisão de Estoque", link: "/revisao-estoque",    icon: <ShieldCheck size={14} /> },
                            hasPerm("/transferencia-estoque") && { text: "Transferência de Estoque", link: "/transferencia-estoque", icon: <ArrowRightLeft size={14} /> },
                            hasPerm("/entrada-estoque")   && { text: "Entrada Estoque",    link: "/entrada-estoque",    icon: <PackagePlus size={14} /> },
                            hasPerm("/promocoes")         && { text: "Promoções",          link: "/promocoes",          icon: <MdDiscount size={14} /> },
                            isAdmin                       && { text: "Publicidade ML",     link: "/publicidade-ml",     icon: <Megaphone size={14} /> },
                            isAdmin                       && { text: "Saúde dos Anúncios ML", link: "/saude-anuncios", icon: <HeartPulse size={14} /> },
                            isAdmin                       && { text: "Métricas de Anúncios", link: "/metricas-anuncios", icon: <BarChart3 size={14} /> },
                          ].filter(Boolean)}
                          {...nav}
                        />
                      )}
                      {["/banco-imagens","/imagens-bloqueadas"].some(hasPerm) && (
                        <NavItem
                          icon={<ImageIcon size={20} />} text="Imagens" hasSubmenu
                          open={openSubmenu.imagens} toggleSubmenu={() => toggleSubmenu("imagens")}
                          sublinks={[
                            hasPerm("/banco-imagens")      && { text: "Banco de Imagens",  link: "/banco-imagens",             icon: <ImageIcon size={14} /> },
                            hasPerm("/banco-imagens")      && { text: "Imagens Excluídas", link: "/banco-imagens?excluidas=1", icon: <Trash2 size={14} /> },
                            hasPerm("/banco-imagens")      && { text: "Logs de Imagens",   link: "/logs-imagens",              icon: <Logs size={14} /> },
                            hasPerm("/imagens-bloqueadas") && { text: "Imagens Bloqueadas",link: "/imagens-bloqueadas",         icon: <Ban size={14} /> },
                          ].filter(Boolean)}
                          {...nav}
                        />
                      )}
                      {(hasPerm("/dimensoes") || isAdmin) && (
                        <NavItem icon={<Ruler size={20} />} text="Dimensões" link="/dimensoes" hasSubmenu={false} {...nav} />
                      )}
                    </>
                  )}

                  {/* ── Clientes ── */}
                  {["/clientes","/crm","/prospeccao","/mensageria","/perguntas","/cotacoes-email","/assistente-vendas","/chat-ia","/discador","/ura"].some(hasPerm) && (
                    <>
                      {isOpen && <SectionLabel>Clientes</SectionLabel>}
                      {["/clientes", "/crm", "/prospeccao"].some(hasPerm) && (
                        <NavItem
                          icon={<Users size={20} />} text="Clientes" hasSubmenu
                          open={openSubmenu.clientes} toggleSubmenu={() => toggleSubmenu("clientes")}
                          sublinks={[
                            hasPerm("/clientes")   && { text: "Clientes",   link: "/clientes",   icon: <Users size={14} /> },
                            hasPerm("/crm")        && { text: "CRM",        link: "/crm",        icon: <Headset size={14} /> },
                            hasPerm("/prospeccao") && { text: "Prospecção", link: "/prospeccao", icon: <Search size={14} /> },
                          ].filter(Boolean)}
                          {...nav}
                        />
                      )}
                      {/* Canais por onde o cliente fala com a gente. Solicitações é a
                          fila do que o bot não resolveu sozinho, por isso fica aqui,
                          mas a permissão continua sendo a do assistente. */}
                      {["/mensageria", "/perguntas", "/cotacoes-email", "/assistente-vendas"].some(hasPerm) && (
                        <NavItem
                          icon={<Headset size={20} />} text="Atendimento" hasSubmenu
                          open={openSubmenu.atendimento} toggleSubmenu={() => toggleSubmenu("atendimento")}
                          sublinks={[
                            hasPerm("/mensageria")        && { text: "Mensageria",          link: "/mensageria",     icon: <MessageSquare size={14} /> },
                            hasPerm("/perguntas")         && { text: "Perguntas",           link: "/perguntas",      icon: <MessageCircleQuestion size={14} /> },
                            hasPerm("/cotacoes-email")    && { text: "Cotações por E-mail", link: "/cotacoes-email", icon: <Mail size={14} /> },
                            hasPerm("/assistente-vendas") && { text: "Solicitações",        link: "/solicitacoes",   icon: <Bot size={14} /> },
                          ].filter(Boolean)}
                          {...nav}
                        />
                      )}
                      {["/discador", "/ura"].some(hasPerm) && (
                        <NavItem
                          icon={<Phone size={20} />} text="Telefone" hasSubmenu
                          open={openSubmenu.telefone} toggleSubmenu={() => toggleSubmenu("telefone")}
                          sublinks={[
                            hasPerm("/discador") && { text: "Discador NVOIP",  link: "/discador", icon: <Phone size={14} /> },
                            hasPerm("/ura")      && { text: "URA do telefone", link: "/ura",      icon: <PhoneCall size={14} /> },
                          ].filter(Boolean)}
                          {...nav}
                        />
                      )}
                      {/* Chat IA tem permissão própria (/chat-ia), separada da do assistente. */}
                      {["/assistente-vendas", "/chat-ia"].some(hasPerm) && (
                        <NavItem
                          icon={<Bot size={20} />} text="Bot Poli (IA)" hasSubmenu
                          open={openSubmenu.assistenteIA} toggleSubmenu={() => toggleSubmenu("assistenteIA")}
                          sublinks={[
                            hasPerm("/assistente-vendas") && { text: "Assistente Poli", link: "/assistente-vendas", icon: <MessageSquare size={14} /> },
                            hasPerm("/chat-ia")           && { text: "Chat IA",          link: "/chat-ia",           icon: <Bot size={14} /> },
                            hasPerm("/assistente-vendas") && { text: "Bot Atendimento",  link: "/bot-atendimento",   icon: <Bot size={14} /> },
                            // Custos de IA e teste A/B: páginas restritas a admins (AdminOnly na rota)
                            isAdmin && { text: "Custos IA",        link: "/bot-custos-ia", icon: <DollarSign size={14} /> },
                            isAdmin && { text: "Teste A/B do Bot", link: "/bot-teste-ab",  icon: <FlaskConical size={14} /> },
                          ].filter(Boolean)}
                          {...nav}
                        />
                      )}
                    </>
                  )}

                  {/* ── Integrações ── */}
                  {["/melhor-envio", "/lalamove"].some(hasPerm) && (
                    <>
                      {isOpen && <SectionLabel>Integrações</SectionLabel>}
                      <NavItem
                        icon={<Unplug size={20} />} text="Integrações" hasSubmenu
                        open={openSubmenu.integracoes} toggleSubmenu={() => toggleSubmenu("integracoes")}
                        sublinks={[
                          hasPerm("/melhor-envio") && { text: "Melhor Envio", link: "/melhor-envio", icon: <img src="/melhor_envio_logo.webp" alt="ME" style={{ height: "14px", width: "auto", objectFit: "contain" }} /> },
                          hasPerm("/lalamove")     && { text: "Lalamove", link: "/lalamove", icon: <img src="/lalamove_logo.webp" alt="Lalamove" style={{ height: "12px", width: "auto", objectFit: "contain" }} /> },
                        ].filter(Boolean)}
                        {...nav}
                      />
                    </>
                  )}

                  {/* ── Suporte ── */}
                  {hasPerm("/tickets") && (
                    <>
                      {isOpen && <SectionLabel>Suporte</SectionLabel>}
                      <NavItem
                        icon={<Ticket size={20} style={{ transform: "scaleX(-1)" }} />} text="Tickets" hasSubmenu
                        open={openSubmenu.tickets} toggleSubmenu={() => toggleSubmenu("tickets")}
                        sublinks={[
                          { text: "Tickets",     link: "/tickets",     icon: <Ticket size={14} style={{ transform: "scaleX(-1)" }} /> },
                          { text: "Novo Ticket", link: "/tickets/novo",icon: <TicketPlus size={14} style={{ transform: "scaleX(-1)" }} /> },
                        ]}
                        {...nav}
                      />
                    </>
                  )}

                  {/* ── Relatórios ── */}
                  {["/exportar_produtos","/exportar_clientes","/exportar_vendas","/exportar_conversas","/exportar_listas_compras"].some(hasPerm) && (
                    <>
                      {isOpen && <SectionLabel>Relatórios</SectionLabel>}
                      <NavItem
                        icon={<ChartNoAxesCombined size={20} />} text="Relatórios" hasSubmenu
                        open={openSubmenu.relatorios} toggleSubmenu={() => toggleSubmenu("relatorios")}
                        downloadingReport={downloadingReport}
                        sublinks={[
                          { id: "diario",           text: "Vendas Diário",   onClick: () => handleDownload("diario"),  icon: <FileChartLine size={14} /> },
                          { id: "mensal",           text: "Vendas Mensal",   onClick: () => handleDownload("mensal"),  icon: <FileChartColumnIncreasing size={14} /> },
                          hasPerm("/exportar_produtos") && { id: "exportar_produtos",text: "Produtos", link: "/exportar_produtos", icon: <TableProperties size={14} /> },
                          hasPerm("/exportar_clientes") && { id: "exportar_clientes",text: "Clientes", link: "/exportar_clientes", icon: <UsersRound size={14} /> },
                          hasPerm("/exportar_vendas")   && { id: "exportar_vendas",  text: "Vendas",   link: "/exportar_vendas",   icon: <ShoppingCart size={14} /> },
                          hasPerm("/exportar_conversas") && { id: "exportar_conversas", text: "Conversas", link: "/exportar_conversas", icon: <MessageSquare size={14} /> },
                          hasPerm("/exportar_listas_compras") && { id: "exportar_listas_compras", text: "Listas de Compras", link: "/exportar_listas_compras", icon: <ListChecks size={14} /> },
                          isAdmin && { id: "exportar_movimentacao_estoque", text: "Estoque", link: "/exportar_movimentacao_estoque", icon: <ArrowRightLeft size={14} /> },
                          isAdmin && { id: "exportar_xml_nfe", text: "XML NF-e", link: "/notas_fiscais?aba=exportar", icon: <ReceiptText size={14} /> },
                        ].filter(Boolean)}
                        {...nav}
                      />
                    </>
                  )}

                  {/* ── Sistema ── */}
                  {["/usuarios","/log-acessos","/servidor","/configuracoes","/wiki","/guias","/logs","/logs_produtos"].some(hasPerm) && (
                    <>
                      {isOpen && <SectionLabel>Sistema</SectionLabel>}
                      {["/usuarios", "/log-acessos"].some(hasPerm) && (
                        <NavItem
                          icon={<UsersRound size={20} />} text="Usuários" hasSubmenu
                          open={openSubmenu.usuarios} toggleSubmenu={() => toggleSubmenu("usuarios")}
                          sublinks={[
                            hasPerm("/usuarios")    && { text: "Usuários",          link: "/usuarios",    icon: <UsersRound size={14} /> },
                            hasPerm("/log-acessos") && { text: "Gestão de Acessos", link: "/log-acessos", icon: <ShieldCheck size={14} /> },
                          ].filter(Boolean)}
                          {...nav}
                        />
                      )}
                      {hasPerm("/configuracoes") && <NavItem icon={<Settings size={20} />} text="Configurações" link="/configuracoes" hasSubmenu={false} {...nav} />}
                      {["/servidor", "/logs", "/logs_produtos"].some(hasPerm) && (
                        <NavItem
                          icon={<FaComputer size={18} />} text="Monitoramento" hasSubmenu
                          open={openSubmenu.monitoramento} toggleSubmenu={() => toggleSubmenu("monitoramento")}
                          sublinks={[
                            hasPerm("/servidor")      && { text: "Servidor",      link: "/servidor",      icon: <FaComputer size={14} /> },
                            hasPerm("/logs")          && { text: "Logs Sistema",  link: "/logs",          icon: <Logs size={14} /> },
                            hasPerm("/logs_produtos") && { text: "Logs Produtos", link: "/logs_produtos", icon: <Logs size={14} /> },
                          ].filter(Boolean)}
                          {...nav}
                        />
                      )}
                      {["/wiki", "/guias"].some(hasPerm) && (
                        <NavItem
                          icon={<BookMarked size={20} />} text="Ajuda" hasSubmenu
                          open={openSubmenu.ajuda} toggleSubmenu={() => toggleSubmenu("ajuda")}
                          sublinks={[
                            hasPerm("/wiki")  && { text: "Wiki",  link: "/wiki",  icon: <BookMarked size={14} /> },
                            hasPerm("/guias") && { text: "Guias", link: "/guias", icon: <BookMarked size={14} /> },
                          ].filter(Boolean)}
                          {...nav}
                        />
                      )}
                    </>
                  )}
                </VStack>
              )}
            </Box>

            {/* ── Botão expandir (collapsed, desktop) ── */}
            {!isOpen && !isMobile && (
              <Tooltip label="Expandir menu" placement="right">
                <Flex
                  align="center" justify="center"
                  w="40px" h="36px" mx="auto" mb={3} borderRadius="lg"
                  cursor="pointer" color={MUTED}
                  _hover={{ bg: HOVER_BG, color: "white" }}
                  onClick={toggleSidebar} onMouseEnter={toggleSidebar} transition="all 0.15s"
                  flexShrink={0}
                >
                  <ChevronLeft size={16} style={{ transform: "rotate(180deg)" }} />
                </Flex>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default Sidebar;
